import type {GraphAtom} from "./GraphAtom";
import type {GraphBond} from "./GraphBond";

type BondLookup = (atom: GraphAtom) => GraphBond[];

/** Sort atoms by descending connectivity (most-connected first). */
const getSortedGraph = (atoms: GraphAtom[], getBonds: BondLookup): GraphAtom[] => {
    return atoms.slice().sort((a1, a2) => getBonds(a2).length - getBonds(a1).length);
};

/** Build a spanning tree — returns adjacency sets for each atom. */
const getSpanningTree = (graph: GraphAtom[], getBonds: BondLookup): Map<GraphAtom, Set<GraphAtom>> => {
    const tree = new Map<GraphAtom, Set<GraphAtom>>();
    for (const node of graph) tree.set(node, new Set());

    const visited = new Set<GraphAtom>();
    for (const node of graph) {
        for (const bond of getBonds(node)) {
            const child = bond.getOtherAtom(node);
            if (!visited.has(child)) {
                visited.add(child);
                tree.get(node)!.add(child);
                tree.get(child)!.add(node);
            }
        }
    }
    return tree;
};

/** Find edges not in the spanning tree (back edges that form cycles). */
const getRejectedBonds = (graph: GraphAtom[], tree: Map<GraphAtom, Set<GraphAtom>>, getBonds: BondLookup): [GraphAtom, GraphAtom][] => {
    const rejected: [GraphAtom, GraphAtom][] = [];
    const seen = new Set<string>();

    for (const node of graph) {
        for (const bond of getBonds(node)) {
            const child = bond.getOtherAtom(node);
            if (!tree.get(node)!.has(child)) {
                const key = `${node.index}-${child.index}`;
                const reverseKey = `${child.index}-${node.index}`;
                if (!seen.has(key) && !seen.has(reverseKey)) {
                    rejected.push([node, child]);
                    seen.add(key);
                }
            }
        }
    }
    return rejected;
};

/** DFS through the spanning tree to find a path from start to end. */
const findRing = (
    start: GraphAtom,
    end: GraphAtom,
    tree: Map<GraphAtom, Set<GraphAtom>>,
    visited = new Set<GraphAtom>(),
    parents = new Map<GraphAtom, GraphAtom | undefined>(),
    current: GraphAtom = start,
    parent: GraphAtom | undefined = undefined
): GraphAtom[] | undefined => {
    visited.add(current);
    parents.set(current, parent);

    for (const destination of tree.get(current)!) {
        if (destination === end) {
            return getRingPath(start, end, current, parents);
        }
        if (destination === parents.get(current)) continue;
        if (!visited.has(destination)) {
            const ring = findRing(start, end, tree, visited, parents, destination, current);
            if (ring) return ring;
        }
    }
    return undefined;
};

/** Trace back through parents to reconstruct the ring path. */
const getRingPath = (
    start: GraphAtom,
    end: GraphAtom,
    current: GraphAtom,
    parents: Map<GraphAtom, GraphAtom | undefined>
): GraphAtom[] => {
    const ring = [end];
    while (current !== start) {
        ring.push(current);
        const p = parents.get(current);
        if (p === undefined) return [];
        current = p;
    }
    ring.push(start);
    return ring;
};

/** Remove smaller rings that are subsets of larger rings (fused ring cleanup). */
const removeOverlapRings = (rings: GraphAtom[][], getBonds: BondLookup): void => {
    for (let i1 = 0; i1 < rings.length; i1++) {
        for (let i2 = i1 + 1; i2 < rings.length; i2++) {
            let bigger = rings[i1];
            let smaller = rings[i2];
            if (smaller.length > bigger.length) {
                bigger = rings[i2];
                smaller = rings[i1];
            }

            const isSubset = smaller.every(a => bigger.includes(a));
            if (!isSubset) continue;

            const overlapAtoms: GraphAtom[] = [];
            for (const a of smaller) {
                const hasNonOverlapBond = getBonds(a).some(b => {
                    const other = b.getOtherAtom(a);
                    return !smaller.includes(other) && bigger.includes(other);
                });
                if (hasNonOverlapBond) overlapAtoms.push(a);
            }

            for (const a of smaller) {
                if (!overlapAtoms.includes(a)) {
                    const idx = bigger.indexOf(a);
                    if (idx !== -1) bigger.splice(idx, 1);
                }
            }
        }
    }
};

/** Detect all rings in the molecular graph using spanning-tree back edges. */
export const detectRings = (atoms: GraphAtom[], getBonds: BondLookup): GraphAtom[][] => {
    const graph = getSortedGraph(atoms, getBonds);
    const tree = getSpanningTree(graph, getBonds);
    const rejected = getRejectedBonds(graph, tree, getBonds);

    const rings: GraphAtom[][] = [];
    for (const [start, end] of rejected) {
        const ring = findRing(start, end, tree);
        if (ring) {
            rings.push(ring);
        } else {
            // If no ring found, add edge to spanning tree for subsequent searches
            tree.get(start)!.add(end);
            tree.get(end)!.add(start);
        }
    }

    removeOverlapRings(rings, getBonds);
    return rings;
};
